import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { SearchField } from '@/components/ui/search-field';
import { Card } from '@/components/ui/card';
import { PageTitle } from '@/components/ui/text';
import { EmptyState } from '@/components/ui/empty-state';
import { IconButton } from '@/components/ui/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ProjectCard } from '@/components/dashboard/project-card';
import { DashboardHero } from '@/components/dashboard/dashboard-hero';
import { MaintenanceBanner } from '@/components/dashboard/maintenance-banner';
import { isMobileDevice } from '@/utils/deviceDetection';
import { cn } from '@/utils/utils';
import { desktopOnlyCopy } from '@/components/handoff/desktopOnlyCopy';
import { RecentActivity } from '@/components/dashboard/recent-activity';
import { TemplatesCarousel } from '@/components/dashboard/templates-carousel';
import { LoginDialog } from '@/components/dialogs/login-dialog';
import { NewProjectDialog } from '@/components/dialogs/new-project-dialog';
import { MobileNav } from '@/components/layout/mobile-nav';
import { Pagination } from '@/components/cms/ui/pagination';
import { mockAnalytics } from '@/data/mock-data';
import { Project } from '@/types/cmsTypes';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useOrganization } from '@/hooks/useOrganization';
import { useTemplateScenes, usePendingTemplateLink } from '@/modules/templateScenes';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import LureoButton from '@/common/LureoButton';
import Filter from '@/common/Filter';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import lureoLogo from '@/assets/Lureo_Beta_2.png';
import lureoLogoScrolled from '@/assets/Lureo_Beta_6.png';
import { ThemedLogo, useThemeMode } from '@/modules/theme';
import SortSelect from '@/common/SortSelect';
import { MagnifyingGlassIcon, ChatCenteredDotsIcon, DeviceMobileIcon, CalendarPlusIcon, DiscordLogoIcon, PlayCircleIcon, CompassIcon, CubeIcon, MoonIcon, SunIcon } from '@phosphor-icons/react';
import { useNotifications, NotificationPanel, NotificationCountBadge } from '@/modules/notificationService';
import { Panel, PanelContent, PanelTrigger } from '@/components/ui/panel';
import { CollapsibleHeading } from '@/components/ui/disclosure';
import { Avatar } from '@/common/Avatar';
import { PlanSelectionModal } from '@/components/dialogs/plan-selection-modal';
import { VoucherRedeemModal } from '@/components/dialogs/voucher-redeem-modal';
import { AppDownloadDialog } from '@/components/dialogs/app-download-dialog';
import { BookCallDialog } from '@/components/dialogs/book-call-dialog';
import { DiscordDialog } from '@/components/dialogs/discord-dialog';

import { toast } from 'sonner';
import { useEnvironmentPreferences } from '@/hooks/useEnvironmentPreferences';
import { TemplateScene } from '@/types/cmsTypes';
import { TemplateSceneModal } from '@/components/dialogs/template-scene-modal';
import { UserFeedbackDialog } from '@/components/dialogs/user-feedback-dialog';
import { UserInfoOnboardingDialog, type UserInfoOnboardingData } from '@/components/dialogs/user-info-onboarding-dialog';
import { DeleteDialog } from '@/components/dialogs/scene-project-delete-dialog';
import publicAppQRCodeIosAndroid from '@/assets/QR_code_public_ios_android.png';
import { useCmsDashboardTour, CmsDashboardTour } from '@/modules/cmsOnboarding';
import { useDebounce } from '@/modules/assetSearch/hooks';
import { PageFrame } from '@/components/ui/page-frame';
import { convertToProjectFormat, type ProjectStatus } from './dashboard/projectCards';
import { useDashboardProjects } from './dashboard/useDashboardProjects';
import { useTemplateGallery } from './dashboard/useTemplateGallery';
import { useNewStory } from './dashboard/useNewStory';
import { saveOnboardingAnswers } from './dashboard/saveOnboardingAnswers';

const PROJECT_SEARCH_DEBOUNCE_MS = 150;

// The hero's round buttons: an on-emphasis ring and glyph in every state, over the hero or the frosted nav

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const services = useServices();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, PROJECT_SEARCH_DEBOUNCE_MS);
  const [filterStatus, setFilterStatus] = useState<'all' | ProjectStatus>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | 'newest' | 'oldest'>('newest');
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'live', label: 'Live' },
    { value: 'pending', label: 'Pending' },
    { value: 'archived', label: 'Archived' }
  ];
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  // Plan selection modals
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const [showBookCallDialog, setShowBookCallDialog] = useState(false);
  const [showDiscordDialog, setShowDiscordDialog] = useState(false);
  const [showUserFeedbackDialog, setShowUserFeedbackDialog] = useState(false);
  const [showUserInfoOnboardingDialog, setShowUserInfoOnboardingDialog] = useState(false);
  const { orgInstance, orgMember, loading: orgLoading, error: orgError, refresh: refreshOrg } = useOrganization();
  const orgUnavailable = !!user && !orgLoading && !orgInstance;
  const [isScrolled, setIsScrolled] = useState(false);
  const { mode: themeMode, toggle: toggleThemeMode } = useThemeMode();
  const [isAccountExpanded, setIsAccountExpanded] = useState(true); // Start expanded
  const { notifications, markAsRead, markAllAsRead, refresh: refreshNotifications } = useNotifications(services.notification);
  const unreadCount = notifications.filter(n => !n.read).length;
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateScene | null>(null);
  const [isTemplatesExpanded, setIsTemplatesExpanded] = useState(true);
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; title: string; type: 'project' | 'scene' } | null>(null);
  
  // Fetch template scenes using the service
  const { templateScenes, loading: templatesLoading, error: templatesError, refresh: refreshTemplates } = useTemplateScenes();
  const gallery = useTemplateGallery(templateScenes);
  const { environmentPreferences } = useEnvironmentPreferences();
  const userIsAdmin = services.subscriptionPlan.canOverrideEnvPrefsToViewTemplates(orgInstance?.orgInstanceSubscriptionPlan ?? 'starter');

  // Auto-expand account button for 3 seconds on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAccountExpanded(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Track scroll position for frosted navbar effect
  useEffect(() => {
    const handleScroll = () => {
      // Use body.scrollTop since body is the scrolling element
      const scrollPosition = document.body.scrollTop || window.scrollY || document.documentElement.scrollTop;
      const scrolled = scrollPosition > 20;
      setIsScrolled(scrolled);
    };

    // Listen to body scroll since that's what's actually scrolling
    document.body.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll);
    
    handleScroll(); // Check initial position
    
    return () => {
      document.body.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const needsUserInfoOnboarding =
    !!orgMember &&
    (orgMember.persona == null || String(orgMember.persona).trim() === '') &&
    (orgMember.commercialIntent == null || String(orgMember.commercialIntent).trim() === '');

  // Show user info onboarding when both persona and commercial_intent are empty
  const [onboardingCheck, setOnboardingCheck] = useState<{
    orgLoading: boolean;
    user: typeof user;
    orgMember: typeof orgMember;
    needsUserInfoOnboarding: boolean;
  } | null>(null);
  if (
    !onboardingCheck ||
    onboardingCheck.orgLoading !== orgLoading ||
    onboardingCheck.user !== user ||
    onboardingCheck.orgMember !== orgMember ||
    onboardingCheck.needsUserInfoOnboarding !== needsUserInfoOnboarding
  ) {
    setOnboardingCheck({ orgLoading, user, orgMember, needsUserInfoOnboarding });
    if (!orgLoading && user && orgMember && needsUserInfoOnboarding) {
      setShowUserInfoOnboardingDialog(true);
    }
  }

  // Creator affiliate links open their template after the onboarding questions
  const { arrivedViaTemplateLink } = usePendingTemplateLink({
    templateScenes,
    templatesLoading,
    blocked: orgLoading || !orgMember || needsUserInfoOnboarding || showUserInfoOnboardingDialog,
    onOpen: setSelectedTemplate
  });


  // Enable scrolling for dashboard
  useLayoutEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    
    document.body.style.setProperty('overflow', 'auto', 'important');
    document.documentElement.style.setProperty('overflow', 'auto', 'important');

    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  // DONT SHOW THIS FOR NOW AS USERS ARE SIGNING UP WITH VOUCHER CODE
  // Dashboard gating: Auto-open plan modal for new accounts
  // useEffect(() => {
  //   if (!orgLoading && orgInstance) {
  //     const accountAge = Date.now() - new Date(orgInstance.createdAt).getTime();
  //     const fiveMinutes = 5 * 60 * 1000;
  //     const thisYear = new Date().getFullYear();
      
  //     // Show modal if account less than 5 minutes old and still on starter
  //     if (accountAge < fiveMinutes && orgInstance.orgInstanceSubscriptionPlan === 'starter') {
  //       setShowPlanModal(true);
  //     } else if (orgInstance.orgInstanceSubscriptionPlan === 'starter') {
  //       // If starter plan but not new, show banner
  //       setShowPlanBanner(true);
  //     }
  //   }
  // }, [orgInstance, orgLoading]);

  const userId = user?.id;
  const {
    projects: currentProjects,
    accountTotal,
    totalPages,
    currentPage,
    setCurrentPage,
    loading,
    hasLoaded: hasLoadedProjects,
    failed: projectsFailed,
    retry: retryProjects,
    removeProject
  } = useDashboardProjects({ userId, search: debouncedSearchQuery, sort: sortOrder, filterKey: filterStatus });

  // The list endpoint has no status filter yet, so a status narrows the page already read
  const convertedProjects = useMemo(() => {
    const converted = currentProjects.map(convertToProjectFormat);
    return filterStatus !== 'all' ? converted.filter((project) => project.status === filterStatus) : converted;
  }, [currentProjects, filterStatus]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const { createStory: handleCreateProject, isCreatingStory, forgetProject } = useNewStory({
    userId,
    orgInstance,
    orgLoading,
    orgError,
    retryOrg: refreshOrg,
    onSignInNeeded: () => setShowLoginDialog(true)
  });

  const handleViewProject = (project: Project) => {
    navigate(`/project/${project.id}`);
  };

  const handleDeleteProject = (project: Project) => {
    setItemToDelete({
      id: project.id,
      title: project.title,
      type: 'project'
    });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (item: { id: string; title: string; type: 'project' | 'scene' }) => {
    if (!user?.id) return;

    if (item.type === 'project') {
      try {
        await services.project.deleteProject(item.id);
        forgetProject(item.id);
        removeProject(item.id);
        toast.success('Project deleted successfully');
      } catch (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
      }
    }
  };

  const handleSelectTemplate = (template: TemplateScene) => {
    setSelectedTemplate(template);
  };

  const handleMyAccount = () => {
    navigate('/account');
  };

  // Refresh org data after voucher redemption
  const handleVoucherSuccess = () => {
    // Reload the page to fetch updated org data
    window.location.reload();
  };

  // Show loading spinner until all data is loaded
  // For users with 0 projects, consider fully loaded as soon as projects have loaded
  // (no need to wait for project conversion which is a no-op when empty)
  const isFullyLoaded = !!user && !loading && hasLoadedProjects && accountTotal !== null;

  const templatesVisible = !!(environmentPreferences?.templates || userIsAdmin);

  const { run: runDashboardTour, steps: dashboardSteps, handleJoyrideCallback: handleDashboardTourCallback, startTour } =
    useCmsDashboardTour(accountTotal ?? 0, isFullyLoaded, templatesVisible, showUserInfoOnboardingDialog, arrivedViaTemplateLink);

  return (
    <PageFrame layout="content" className="cursor-auto select-none">
      <CmsDashboardTour
        run={runDashboardTour}
        steps={dashboardSteps}
        onCallback={handleDashboardTourCallback}
      />
      <MaintenanceBanner />
      {/* Mobile/Tablet Navigation */}
      <MobileNav
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filterStatus={filterStatus}
        onFilterChange={setFilterStatus}
        sortOrder={sortOrder}
        onSortChange={setSortOrder}
        onMyAccount={handleMyAccount}
      />

      {/* Frosted Glass Navbar - Shows when scrolled (background + logo only;
          notif/account buttons render once below and stay pinned regardless of scroll). */}
      {isScrolled && (
        <div className="fixed left-0 right-0 top-0 z-lureo-layer-bar hidden h-[4.5rem] surface-bar transition-all duration-lureo-duration-slow lg:block">
          <ThemedLogo src={themeMode === 'dark' ? lureoLogo : lureoLogoScrolled} alt="Lureo" className="absolute left-6 top-4 h-10" />
        </div>
      )}

      {/* Desktop Header - Shows when not scrolled, hidden on mobile */}
      <div
        className={`hidden lg:block absolute left-6 top-4 z-10 transition-opacity duration-lureo-duration-slow ${
          isScrolled ? 'opacity-0' : 'opacity-100'
        }`}>
        <div className="relative">
          {/* Original logo */}
          <ThemedLogo src={lureoLogo} alt="Lureo" className="h-10" />
          {/* White logo clipped to beta text area; a theme that draws the logo's shape draws this part too */}
          <img
            src={lureoLogo}
            alt=""
            className="theme-logo-overlay absolute inset-0 h-10 brightness-0 invert"
            style={{
              clipPath: 'inset(0 0 70% 70%)'
            }}
          />
        </div>
      </div>

      {/* Desktop My Account + Notifications — rendered once at right-6 top-4 so
          they stay at the exact same viewport coordinates whether the page is scrolled
          or not. Only the notif button's fill flips: white-ring on the hero, dark-purple
          (matches the Avatar) over the frosted nav. Absolute, not fixed: nothing above it is
          positioned, so it holds still as the body scrolls, and the tour's spotlight (Joyride)
          lands on it; Joyride misplaces a fixed target inside a scrolling body. */}
      <div className="hidden lg:flex absolute right-6 top-4 z-lureo-layer-overlay items-center gap-2">
        {userIsAdmin && (
          <Tooltip>
            <TooltipTrigger asChild>
              <IconButton place="hero" label="Bulk asset upload" onClick={() => navigate('/admin/asset-upload')} className={isScrolled ? 'bg-lureo-secondary' : undefined}>
                <CubeIcon className="h-5 w-5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={10}>
              Bulk asset upload
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton place="hero" label={themeMode === 'dark' ? 'Light mode' : 'Dark mode'} onClick={toggleThemeMode} className={isScrolled ? 'bg-lureo-secondary' : undefined}>
              {themeMode === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </IconButton>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={10}>
            {themeMode === 'dark' ? 'Light mode' : 'Dark mode'}
          </TooltipContent>
        </Tooltip>
        <Panel
          open={showNotifications}
          onOpenChange={next => {
            setShowNotifications(next);
            if (next) refreshNotifications();
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PanelTrigger asChild>
                {/* On hover it fills with the call to action's gradient, as it always has */}
                <IconButton place="hero" label="Notifications" disabled={notifications.length === 0} className={cn('relative hover:bg-linear-[var(--lureo-cta-angle)_in_oklab] hover:from-lureo-cta hover:from-25% hover:via-lureo-cta-to/90 hover:via-80% hover:to-lureo-cta-to', isScrolled && 'bg-lureo-secondary')}>
                  <ChatCenteredDotsIcon className="h-5 w-5" />
                  <NotificationCountBadge count={unreadCount} className="pointer-events-none absolute -right-2 -top-1.5" />
                </IconButton>
              </PanelTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={10}>
              Notifications
            </TooltipContent>
          </Tooltip>
          <PanelContent width="md" align="end">
            <NotificationPanel notifications={notifications} markAsRead={markAsRead} markAllAsRead={markAllAsRead} />
          </PanelContent>
        </Panel>

        <div
          data-tour="my-account"
          className="h-9"
          onMouseEnter={() => setIsAccountExpanded(true)}
          onMouseLeave={() => setIsAccountExpanded(false)}>
        <LureoButton
          variant="outline-ink-strong"
            className={`group relative h-9 overflow-hidden rounded-lureo-radius-avatar bg-lureo-on-emphasis-subtle px-3 transition-all duration-lureo-duration-slow ease-lureo-easing-standard ${
              isAccountExpanded ? 'w-36 px-16' : 'w-9 hover:w-36 hover:px-16'
            } text-lureo-on-cta hover:cta-fill`}
          onClick={handleMyAccount}>
            <div className={`absolute left-0.5 top-0.5 transition-all duration-lureo-duration-slow ease-lureo-easing-standard ${
              isAccountExpanded ? 'left-1' : ''
            }`}>
            <Avatar className="h-8 w-8" size="sm" onHero />
          </div>
            <span className={`absolute left-10 whitespace-nowrap transition-all duration-lureo-duration-slow ease-lureo-easing-standard ${
              isAccountExpanded ? 'opacity-100' : 'opacity-0'
            }`}>
            My Account
          </span>
        </LureoButton>
        </div>
      </div>

      <DashboardHero
        name={orgMember?.fullName?.trim() || (user?.email?.split('@')[0] || 'User').charAt(0).toUpperCase() + (user?.email?.split('@')[0] || 'User').slice(1)}
        onNewStory={handleCreateProject}
        isCreating={isCreatingStory}
        blockedLabel={isMobileDevice() ? desktopOnlyCopy.heading : undefined}
        actions={[
          { label: 'Download', glyph: <DeviceMobileIcon className="h-6 w-6" />, onClick: () => setShowAppDownloadModal(true) },
          { label: 'Demo', glyph: <CalendarPlusIcon className="h-6 w-6" />, onClick: () => setShowBookCallDialog(true) },
          { label: 'Discord', glyph: <DiscordLogoIcon className="h-6 w-6" />, onClick: () => setShowDiscordDialog(true) },
          { label: 'Feedback', glyph: <ChatCenteredDotsIcon className="h-6 w-6" />, onClick: () => setShowUserFeedbackDialog(true) },
          {
            label: 'Tutorials',
            glyph: <PlayCircleIcon className="h-6 w-6" />,
            onClick: () => {
              services.analytics.trackLureoTutorialVideosOpened({ trigger_context: 'dashboard' });
              window.open('https://www.youtube.com/playlist?list=PLgB1wSddYukuypuitJDt4YJbhVHBbpeqC', '_blank');
            },
          },
          { label: 'Tour', glyph: <CompassIcon className="h-6 w-6" />, onClick: () => startTour() },
        ]}
      />

      <div className="container space-y-8 py-6 md:space-y-12 md:py-8">
        {orgUnavailable && (
          <Card padding="lg">
            <EmptyState
              title="We could not load your account details"
              action={<LureoButton variant="secondary" suppressTooltip onClick={() => void refreshOrg()} className="h-9 rounded-lureo-radius-button-pill px-4">Try again</LureoButton>}
            >
              Some things on this page need them. Check your connection and try again.
            </EmptyState>
          </Card>
        )}
        <section data-tour="template-stories">
            <TemplatesCarousel
              templates={gallery.templates}
              loading={templatesLoading}
              error={templatesError}
              onRetry={refreshTemplates}
              onSelectTemplate={handleSelectTemplate}
              searchQuery={gallery.searchQuery}
              onSearchChange={gallery.setSearchQuery}
              difficultyFilter={gallery.difficultyFilter}
              onDifficultyFilterChange={gallery.setDifficultyFilter}
              featureFilter={gallery.featureFilter}
              onFeatureFilterChange={gallery.setFeatureFilter}
              sortOrder={gallery.sortOrder}
              onSortChange={gallery.setSortOrder}
              currentPage={gallery.currentPage}
              totalPages={gallery.totalPages}
              onPageChange={gallery.setCurrentPage}
              isExpanded={isTemplatesExpanded}
              onToggleExpand={() => setIsTemplatesExpanded(!isTemplatesExpanded)}
            />
          </section>

        {/* Projects Section */}
        <section data-tour="projects-section">
          {/* Desktop Header with Search and Filters */}
          <div className="mb-6 hidden items-center justify-between lg:flex">
            <CollapsibleHeading title="Your Projects" open={isProjectsExpanded} onOpenChange={setIsProjectsExpanded} />
            <div className="flex items-center gap-3">
              <SearchField
                size="md"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={setSearchQuery}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                className="w-64"
              />
              <Filter<'all' | ProjectStatus>
                options={statusOptions}
                onFilterChange={setFilterStatus}
                placeholder="Filter by status"
                value={filterStatus}
              />
              <SortSelect
                value={sortOrder}
                onValueChange={(value) => setSortOrder(value as 'asc' | 'desc' | 'newest' | 'oldest')}
              />
            </div>
          </div>

          {/* Mobile/Tablet Header - Simple */}
          <div className="mb-4 lg:hidden">
            <CollapsibleHeading title="Your Projects" open={isProjectsExpanded} onOpenChange={setIsProjectsExpanded} />
            {searchQuery && <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">Search: "{searchQuery}"</p>}
            {filterStatus !== 'all' && <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">Filter: {filterStatus}</p>}
            {sortOrder !== 'newest' && <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">Sort: {sortOrder === 'asc' ? 'A to Z' : sortOrder === 'desc' ? 'Z to A' : sortOrder === 'oldest' ? 'Oldest' : 'Newest'}</p>}
          </div>

          {isProjectsExpanded && (
            <>
              {!hasLoadedProjects ? (
            // Initial loading - show spinner
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="xl" />
            </div>
          ) : (
            <>
              {filterStatus !== 'all' && convertedProjects.length > 0 && (
                <p className="mb-4 text-lureo-14 text-lureo-ink-subtle leading-5">The status filter only looks at the projects on this page.</p>
              )}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-4 lg:gap-8">
                {convertedProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onView={handleViewProject} onDelete={handleDeleteProject} />
                ))}
              </div>

              {/* Pagination - Always visible */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />

              {hasLoadedProjects && projectsFailed && (
                <Card padding="lg">
                  <EmptyState
                    title="Couldn't load your projects"
                    action={<LureoButton variant="secondary" suppressTooltip onClick={retryProjects} className="h-9 rounded-lureo-radius-button-pill px-4">Try again</LureoButton>}
                  >
                    Check your connection and try again.
                  </EmptyState>
                </Card>
              )}

              {hasLoadedProjects && !projectsFailed && convertedProjects.length === 0 && (
                filterStatus !== 'all' && currentProjects.length > 0 ? (
                  <Card padding="lg">
                    <EmptyState icon={<MagnifyingGlassIcon />} title={`No ${filterStatus} projects on this page`}>
                      The status filter only looks at the projects on this page. Try another page.
                    </EmptyState>
                  </Card>
                ) : searchQuery || filterStatus !== 'all' ? (
                  <Card padding="lg">
                    <EmptyState icon={<MagnifyingGlassIcon />} title="No projects found">
                      Try adjusting your search or filter criteria.
                    </EmptyState>
                  </Card>
                ) : (
                  <EmptyState size="roomy">Your stories will appear here.</EmptyState>
                )
              )}
            </>
          )}
          </>
          )}
        </section>

        {/* Analytics Overview */}
        {/* <section>
          <PageTitle as="h2" className="mb-4 md:mb-6">Analytics Overview</PageTitle>
          <AnalyticsOverview analytics={mockAnalytics} />
        </section> */}

        {/* Recent Activity */}
        <section className="hidden lg:block">
          <PageTitle as="h2" className="mb-4 md:mb-6">Recent Activity</PageTitle>
          <RecentActivity activities={mockAnalytics.recentActivity} />
        </section>
      </div>

      {/* Lock the rest of the page while creating a new story or template */}
      {isCreatingStory && (
        <div
          aria-hidden
          className="fixed inset-0 z-lureo-layer-blocker cursor-wait bg-lureo-emphasis/15"
        />
      )}

      {/* Login Dialog */}
      <LoginDialog open={showLoginDialog} onOpenChange={setShowLoginDialog} />

      {/* New Project Dialog */}
      <NewProjectDialog open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog} />

      {/* Plan Selection Modal */}
      <PlanSelectionModal
        open={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        onVoucherClick={() => {
          setShowPlanModal(false);
          setShowVoucherModal(true);
        }}
      />
      
      {/* Voucher Redeem Modal */}
      <VoucherRedeemModal
        open={showVoucherModal}
        onClose={() => setShowVoucherModal(false)}
        onSuccess={handleVoucherSuccess}
      />
      
      {/* App Download Modal */}
      <AppDownloadDialog
        open={showAppDownloadModal}
        onOpenChange={setShowAppDownloadModal}
        qrCodeUrl={publicAppQRCodeIosAndroid}
      />

      {/* Book Call Dialog */}
      <BookCallDialog
        open={showBookCallDialog}
        onOpenChange={setShowBookCallDialog}
      />

      {/* Discord Dialog */}
      <DiscordDialog
        open={showDiscordDialog}
        onOpenChange={setShowDiscordDialog}
      />

      {/* User Feedback Dialog */}
      <UserFeedbackDialog
        open={showUserFeedbackDialog}
        onOpenChange={setShowUserFeedbackDialog}
      />

      {/* User info onboarding – shown when persona and commercial_intent are both empty */}
      <UserInfoOnboardingDialog
        open={showUserInfoOnboardingDialog}
        onOpenChange={setShowUserInfoOnboardingDialog}
        onComplete={async (data: UserInfoOnboardingData) => {
          if (!user?.id) return;
          await saveOnboardingAnswers(services, user.id, data);
          await refreshOrg();
          setShowUserInfoOnboardingDialog(false);
        }}
      />

      {/* Template Scene Modal */}
      <TemplateSceneModal
        open={selectedTemplate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTemplate(null);
          }
        }}
        template={selectedTemplate}
      />

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        item={itemToDelete}
      />
    </PageFrame>
  );
}
